/**
 * Google Analytics 4 mit Consent Gate.
 *
 * Ablauf ("Basic Consent Mode"):
 *  1. `public/index.html` setzt vor allem anderen alle Consent-Signale auf `denied`.
 *  2. Solange keine Einwilligung vorliegt, wird `gtag.js` gar nicht erst geladen —
 *     es geht also KEIN einziger Request an Google raus, auch kein cookieloser Ping.
 *  3. Erst wenn im Cookie-Banner "Akzeptieren" geklickt wurde, wird das Script
 *     nachgeladen, `analytics_storage` auf `granted` gesetzt und getrackt.
 *  4. Bei Widerruf werden die GA-Cookies gelöscht und das Consent-Signal
 *     zurückgesetzt. Ein Reload stellt den ursprünglichen Zustand her.
 */

// Zentral änderbar — Measurement ID kann per Environment Variable überschrieben werden.
export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-P28HCFZRYC';

export const CONSENT_STORAGE_KEY = 'eb-cookie-consent';

/** Version der Einwilligung. Hochzählen, wenn sich Zwecke oder Dienste ändern —
 *  dann wird die alte Einwilligung ungültig und das Banner erscheint erneut. */
export const CONSENT_VERSION = 2;

/** Einwilligungen laufen nach 12 Monaten ab (gängige Aufsichtsbehörden-Praxis). */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** Event, mit dem der Footer-Link das Banner erneut öffnet. */
export const CONSENT_OPEN_EVENT = 'eb:consent-open';

/** Event, das nach jeder Consent-Änderung gefeuert wird. */
export const CONSENT_CHANGED_EVENT = 'eb:consent-changed';

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

let initialised = false;
let configured = false;
let scriptPromise = null;

/* -------------------------------------------------------------------------- */
/* dataLayer / gtag                                                            */
/* -------------------------------------------------------------------------- */

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // gtag pusht bewusst das arguments-Objekt, kein Array — so erwartet es Google.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/* -------------------------------------------------------------------------- */
/* Consent lesen / schreiben                                                   */
/* -------------------------------------------------------------------------- */

/**
 * @returns {'granted'|'denied'|null} null = noch keine (gültige) Entscheidung
 */
export function readConsent() {
  if (typeof window === 'undefined') return null;
  let raw;
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null; // z. B. Safari Private Mode
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Alter Wert aus der Zeit vor dem Consent-Gate ('accepted' / 'rejected').
    // Bewusst als ungültig behandeln — damals gab es kein Tracking, die
    // Einwilligung deckt Google Analytics also nicht ab.
    return null;
  }

  if (!parsed || parsed.version !== CONSENT_VERSION) return null;
  if (parsed.status !== CONSENT_GRANTED && parsed.status !== CONSENT_DENIED) return null;
  if (!parsed.timestamp || Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) return null;

  return parsed.status;
}

function writeConsent(status) {
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ status, version: CONSENT_VERSION, timestamp: Date.now() })
    );
  } catch {
    /* Storage nicht verfügbar — Entscheidung gilt dann nur für diese Session. */
  }
}

/* -------------------------------------------------------------------------- */
/* Script-Loading                                                              */
/* -------------------------------------------------------------------------- */

function loadGtagScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function clearGoogleCookies() {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const domains = [host, `.${host}`];
  const parts = host.split('.');
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);

  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat'))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      });
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
}

/* -------------------------------------------------------------------------- */
/* Aktivieren / Deaktivieren                                                   */
/* -------------------------------------------------------------------------- */

function enableAnalytics({ sendPageView }) {
  gtag('consent', 'update', {
    analytics_storage: CONSENT_GRANTED,
  });

  if (!configured) {
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      // Seitenaufrufe feuern wir selbst — sonst zählt das SPA-Routing falsch.
      send_page_view: false,
    });
    configured = true;
  }

  loadGtagScript().catch(() => {
    /* Adblocker o. ä. — kein Grund, die Seite zu stören. */
  });

  if (sendPageView) sendCurrentPageView();
}

function disableAnalytics() {
  gtag('consent', 'update', {
    analytics_storage: CONSENT_DENIED,
  });
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  clearGoogleCookies();
}

/**
 * Stellt den gespeicherten Consent-Zustand wieder her. Idempotent —
 * kann von überall aufgerufen werden, ohne auf Effect-Reihenfolge zu achten.
 */
export function ensureAnalyticsInitialised() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  if (readConsent() === CONSENT_GRANTED) {
    enableAnalytics({ sendPageView: false });
  }
}

/* -------------------------------------------------------------------------- */
/* Öffentliche API für UI-Komponenten                                          */
/* -------------------------------------------------------------------------- */

export function grantConsent() {
  writeConsent(CONSENT_GRANTED);
  initialised = true;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  enableAnalytics({ sendPageView: true });
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: CONSENT_GRANTED }));
}

export function denyConsent() {
  writeConsent(CONSENT_DENIED);
  initialised = true;
  disableAnalytics();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: CONSENT_DENIED }));
}

/** Öffnet das Banner erneut (Footer-Link "Cookie-Einstellungen"). */
export function openConsentBanner() {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

/* -------------------------------------------------------------------------- */
/* Page Views                                                                  */
/* -------------------------------------------------------------------------- */

let currentPage = null;

function sendCurrentPageView() {
  if (!currentPage) return;
  gtag('event', 'page_view', {
    page_path: currentPage.path,
    page_location: currentPage.location,
    page_title: currentPage.title,
  });
}

/**
 * Meldet einen Seitenaufruf. Wird bei jedem Routenwechsel aufgerufen —
 * gesendet wird nur, wenn eine Einwilligung vorliegt.
 */
export function trackPageView({ path, title } = {}) {
  ensureAnalyticsInitialised();
  currentPage = {
    path: path || window.location.pathname + window.location.search,
    location: window.location.href,
    title: title || document.title,
  };
  if (readConsent() === CONSENT_GRANTED) sendCurrentPageView();
}

/**
 * Beliebiges Custom-Event (z. B. abgeschicktes Kontaktformular).
 * Ohne Einwilligung passiert nichts.
 */
export function trackEvent(name, params = {}) {
  ensureAnalyticsInitialised();
  if (readConsent() !== CONSENT_GRANTED) return;
  gtag('event', name, params);
}
